<template>
  <v-card class="counts-panel" elevation="0">
    <div class="counts-panel__heading">
      <div>
        <h2>Inventory Counts</h2>
        <p>Spreadsheet-style physical counts with the latest comparison shown automatically.</p>
      </div>
      <div class="counts-panel__controls">
        <v-btn v-if="canEdit" color="primary" prepend-icon="mdi-clipboard-plus-outline" @click="openCreateDialog">Start New Count</v-btn>
      </div>
    </div>

    <div v-if="loading" class="counts-panel__empty">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else-if="!selectedCount" class="counts-panel__empty">
      <v-icon color="primary" size="36">mdi-clipboard-text-outline</v-icon>
      <strong>No inventory counts yet</strong>
      <p>Start a count to create the first dated inventory column.</p>
    </div>
    <template v-else>
      <div class="recent-counts">
        <span>Recent counts</span>
        <v-btn
          v-for="count in counts.slice(0, 3).reverse()"
          :key="count.id"
          :color="Number(selectedCount.id) === Number(count.id) ? 'primary' : undefined"
          :variant="Number(selectedCount.id) === Number(count.id) ? 'tonal' : 'outlined'"
          size="small"
          @click="openCount(count.id)"
        >{{ formatDate(count.started_at || count.created_at) }} · {{ scopeLabel(count) }}</v-btn>
      </div>
      <div class="count-toolbar">
        <div class="count-history-nav">
          <v-btn
            :disabled="!canGoOlder"
            aria-label="View older count"
            title="Older count"
            icon="mdi-chevron-left"
            size="small"
            variant="text"
            @click="goToOlderCount"
          ></v-btn>
          <div class="count-history-nav__label">
            <span>{{ formatDate(selectedCount.started_at || selectedCount.created_at) }}</span>
            <small>Count #{{ selectedCount.id }} · {{ scopeLabel(selectedCount) }}</small>
          </div>
          <v-btn
            :disabled="!canGoNewer"
            aria-label="View newer count"
            title="Newer count"
            icon="mdi-chevron-right"
            size="small"
            variant="text"
            @click="goToNewerCount"
          ></v-btn>
          <v-chip :color="statusColor(selectedCount.status)" size="small" variant="tonal">{{ statusLabel(selectedCount.status) }}</v-chip>
        </div>

        <div class="count-toolbar__actions">
          <span v-if="selectedCountIndex === 0" class="latest-label">Latest count</span>
          <v-btn
            v-if="canEdit && isCompleted && isLatestCountOverall && !editingCompletedCount"
            prepend-icon="mdi-pencil-outline"
            size="small"
            variant="tonal"
            @click="beginEditCompletedCount"
          >Edit Count</v-btn>
          <v-btn prepend-icon="mdi-printer-outline" size="small" variant="tonal" @click="printCountSheet">Print Sheet</v-btn>
          <v-btn-toggle v-model="countView" color="primary" density="compact" mandatory variant="outlined">
            <v-btn prepend-icon="mdi-map-marker-outline" value="location">Location</v-btn>
            <v-btn prepend-icon="mdi-shape-outline" value="category">Category</v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <v-alert v-if="isCounting" class="count-alert" color="primary" variant="tonal">
        Enter the physical count in the new dated column. Previous quantities and changes stay hidden until you save the count.
      </v-alert>
      <v-alert v-else-if="isPendingReview" class="count-alert" color="warning" variant="tonal">
        Review the previous and current count before applying it. Applying the count updates live stock and preserves the changes in history.
      </v-alert>
      <v-alert v-else-if="editingCompletedCount" class="count-alert" color="warning" variant="tonal">
        Correct mistaken entries below. Saving applies only the difference from the original count and records the edit in Movement History.
      </v-alert>

      <div v-if="isPendingReview && attentionLines.length" class="count-attention-card">
        <div class="count-attention-card__heading">
          <div>
            <v-icon color="warning" size="21">mdi-alert-circle-outline</v-icon>
            <div>
              <strong>Items Needing Attention</strong>
              <span>Check these items before applying the physical count.</span>
            </div>
          </div>
          <v-chip color="warning" size="small" variant="tonal">{{ attentionLines.length }}</v-chip>
        </div>
        <div class="count-attention-card__items">
          <button
            v-for="line in attentionLines"
            :key="`attention-${line.id}`"
            class="count-attention-item"
            type="button"
            @click="openCountExplanation(line)"
          >
            <div>
              <strong>{{ line.item_name }}</strong>
              <span>Count difference {{ formatDifference(currentStockDifference(line)) }}</span>
            </div>
            <v-chip
              :color="lineReconciliationColor(line)"
              size="x-small"
              variant="tonal"
            >{{ attentionStatusLabel(line) }}</v-chip>
          </button>
        </div>
      </div>

      <div v-if="isCompleted && unresolvedCompletedLines.length" class="count-attention-card count-attention-card--unexplained">
        <div class="count-attention-card__heading">
          <div>
            <v-icon color="warning" size="21">mdi-clipboard-alert-outline</v-icon>
            <div>
              <strong>Unexplained Items</strong>
              <span>Open each item here to clarify it. Resolved items leave this card but remain in Count Findings.</span>
            </div>
          </div>
          <v-chip color="warning" size="small" variant="tonal">{{ unresolvedCompletedLines.length }} unanswered</v-chip>
        </div>
        <div class="count-attention-card__items">
          <button
            v-for="line in unresolvedCompletedLines"
            :key="`unexplained-${line.id}`"
            class="count-attention-item"
            type="button"
            @click="openCountExplanation(line)"
          >
            <div>
              <strong>{{ line.item_name }}</strong>
              <span>Unexplained {{ formatDifference(lineUnexplainedChange(line)) }}</span>
            </div>
            <v-chip color="warning" size="x-small" variant="tonal">Review & Fix</v-chip>
          </button>
        </div>
      </div>

      <div v-if="canEdit && isCompleted && isLatestCountOverall && !editingCompletedCount" class="mobile-edit-count">
        <v-btn block prepend-icon="mdi-pencil-outline" variant="tonal" @click="beginEditCompletedCount">Edit Count</v-btn>
      </div>

      <div class="count-sheet-wrap">
        <table class="count-sheet">
          <thead>
            <tr>
              <th class="item-column">Item</th>
              <th class="number-column previous-column">
                <span>Previous Count</span>
                <small>{{ previousDateLabel }}</small>
              </th>
              <th class="number-column current-column">
                <span>{{ isCounting ? 'New Count' : (editingCompletedCount ? 'Corrected Count' : 'Current Count') }}</span>
                <small>{{ currentDateLabel }}</small>
              </th>
              <th class="number-column change-column">
                <span>Change</span>
                <small>Current − Previous</small>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in groupedCountLines" :key="group.key">
              <tr class="count-group-row">
                <td :colspan="isMobile ? 2 : 4">
                  <div class="count-group-heading">
                    <div>
                      <v-icon size="18">{{ countView === 'location' ? 'mdi-map-marker-outline' : 'mdi-shape-outline' }}</v-icon>
                      <strong>{{ group.label }}</strong>
                    </div>
                    <span>{{ group.lines.length }} {{ group.lines.length === 1 ? 'item' : 'items' }}</span>
                  </div>
                </td>
              </tr>
              <tr
                v-for="line in group.lines"
                :key="line.id"
                :class="['count-item-row', { 'count-item-row--alternate': line.stripeIndex % 2 === 1 }]"
              >
                <td class="item-column">
                  <strong>{{ line.item_name }}</strong>
                  <span>{{ countView === 'location' ? (line.category_name || 'Uncategorized') : (line.location_name || 'No location') }}</span>
                </td>
                <td class="number-column previous-value">
                  <span v-if="isCounting" class="hidden-count">Hidden</span>
                  <strong v-else>{{ displayCount(line.previous_counted_quantity) }}</strong>
                </td>
                <td class="number-column current-column">
                  <v-text-field
                    v-if="canEdit && (isCounting || isPendingReview || editingCompletedCount)"
                    v-model.number="line.countedQuantity"
                    :name="`inventory-physical-count-${line.id}`"
                    autocomplete="off"
                    autocorrect="off"
                    inputmode="numeric"
                    class="count-input"
                    density="compact"
                    hide-details
                    min="0"
                    spellcheck="false"
                    type="number"
                    variant="outlined"
                    @keydown.enter.prevent="focusNextCountInput"
                    @wheel="disableCountWheel"
                  ></v-text-field>
                  <strong v-else>{{ displayCount(line.counted_quantity) }}</strong>
                </td>
                <td :class="['number-column', 'change-column', changeClass(displayedLineChange(line))]">
                  <span v-if="isCounting" class="hidden-count">Hidden</span>
                  <button
                    v-else-if="canExplainLine(line)"
                    class="count-change-button"
                    type="button"
                    :title="lineReconciliationTitle(line)"
                    @click="openCountExplanation(line)"
                  >
                    <strong>{{ formatDifference(displayedLineChange(line)) }}</strong>
                    <v-icon
                      :color="lineReconciliationColor(line)"
                      size="15"
                    >{{ lineReconciliationIcon(line) }}</v-icon>
                  </button>
                  <strong v-else>{{ formatDifference(displayedLineChange(line)) }}</strong>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="isCompleted && completedFindingLines.length" class="completed-findings">
        <button class="completed-findings__heading" type="button" @click="showCompletedFindings = !showCompletedFindings">
          <div>
            <v-icon color="primary" size="19">mdi-clipboard-text-search-outline</v-icon>
            <div>
              <strong>Count Findings</strong>
              <span>Discrepancies and resolutions remain available after the count is completed.</span>
            </div>
          </div>
          <div>
            <v-chip color="primary" size="small" variant="tonal">{{ completedFindingLines.length }}</v-chip>
            <v-icon size="20">{{ showCompletedFindings ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
        </button>
        <div v-if="showCompletedFindings" class="completed-findings__table-wrap">
          <table class="completed-findings__table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Difference</th>
                <th>Resolution</th>
                <th>Observation</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in completedFindingLines" :key="`finding-${line.id}`">
                <td><strong>{{ line.item_name }}</strong></td>
                <td :class="changeClass(line.count_change)"><strong>{{ formatDifference(line.count_change) }}</strong></td>
                <td>{{ completedFindingResolutionLabel(line) }}</td>
                <td>{{ line.notes || '—' }}</td>
                <td>
                  <v-btn
                    :color="completedFindingNeedsReview(line) ? 'orange-darken-2' : 'primary'"
                    size="small"
                    variant="text"
                    @click="openCountExplanation(line)"
                  >
                    {{ completedFindingActionLabel(line) }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="canEdit && isPendingReview && reviewLines.length" class="difference-review">
        <div class="difference-review__heading">
          <div>
            <strong>Review each inventory difference</strong>
            <span>Choose what happened for each item. Correct a mistaken quantity directly in the Current Count column above.</span>
          </div>
          <v-chip size="small" color="warning" variant="tonal">{{ reviewLines.length }} to review</v-chip>
        </div>
        <div
          v-for="line in reviewLines"
          :key="`review-${line.id}`"
          class="difference-review__row"
        >
          <div class="difference-review__identity">
            <strong>{{ line.item_name }}</strong>
            <span>
              Count difference {{ formatDifference(currentStockDifference(line)) }}
              <template v-if="Number(linePendingChange(line) || 0) !== 0">
                · Pending {{ formatDifference(linePendingChange(line)) }}
              </template>
            </span>
          </div>
          <v-select
            v-model="line.resolutionReason"
            :items="resolutionReasonsForLine(line)"
            density="compact"
            hide-details
            label="Resolution"
            variant="outlined"
          ></v-select>
          <v-text-field
            v-model="line.resolutionNotes"
            density="compact"
            hide-details
            label="Optional note"
            variant="outlined"
          ></v-text-field>
        </div>
      </div>

      <div v-if="canEdit && editingCompletedCount" class="count-edit-note">
        <v-text-field
          v-model="editNotes"
          hide-details
          label="Reason or note for this correction (optional)"
          variant="outlined"
        ></v-text-field>
      </div>

      <div v-if="canEdit && (isCounting || isPendingReview || editingCompletedCount)" class="count-save-bar">
        <div>
          <strong>{{ isCounting ? 'Finish this dated count' : (editingCompletedCount ? 'Save corrected quantities' : 'Apply the verified physical quantities') }}</strong>
          <span>{{ isCounting ? 'Every item needs a physical quantity before saving.' : (editingCompletedCount ? 'Only changed entries will create new correction movements.' : 'The completed column remains available in count history.') }}</span>
        </div>
        <v-btn v-if="isCounting" color="primary" :loading="saving" size="large" @click="submitCount">Save Count</v-btn>
        <v-btn v-else-if="isPendingReview" color="primary" :loading="saving" size="large" @click="approveCount">Apply Physical Count</v-btn>
        <div v-else class="count-edit-actions">
          <v-btn :disabled="saving" variant="text" @click="cancelEditCompletedCount">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" size="large" @click="saveCompletedCountCorrection">Save Correction</v-btn>
        </div>
      </div>
    </template>

    <v-dialog v-model="createDialog" max-width="570">
      <v-card>
        <v-card-title class="dialog-title">Start Inventory Count</v-card-title>
        <v-card-text>
          <v-select v-model="createForm.mode" :items="countModes" item-title="label" item-value="value" label="Count type" variant="outlined"></v-select>
          <v-select v-if="createForm.mode === 'category'" v-model="createForm.categoryId" :items="categories" item-title="name" item-value="id" label="Category" variant="outlined"></v-select>
          <v-select v-if="createForm.mode === 'location'" v-model="createForm.locationId" :items="locations" item-title="name" item-value="id" label="Location" variant="outlined"></v-select>
          <v-textarea v-model="createForm.notes" label="Count notes" rows="2" variant="outlined"></v-textarea>
          <v-alert color="primary" variant="tonal">Today’s date is added automatically. Existing quantities remain hidden while the count is open.</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="createDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="createCount">Start Count</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="countExplanation.open" max-width="680">
      <v-card class="count-explanation-card">
        <v-card-title class="count-explanation-heading">
          <div>
            <span>Count Explanation</span>
            <small>{{ countExplanation.line?.item_name }}</small>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="countExplanation.open = false"></v-btn>
        </v-card-title>

        <div v-if="countExplanation.loading" class="count-explanation-loading">
          <v-progress-circular color="primary" indeterminate></v-progress-circular>
        </div>
        <v-card-text v-else-if="countExplanation.data">
          <v-alert v-if="countExplanation.data.baseline" color="primary" variant="tonal">
            This is the first official count for this item, so it becomes the baseline for future explanations.
          </v-alert>
          <template v-else>
            <div class="count-explanation-summary">
              <div>
                <span>Physical Change</span>
                <strong>{{ formatDifference(countExplanation.data.physical_change) }}</strong>
                <small>{{ displayCount(countExplanation.data.previous_count) }} → {{ displayCount(countExplanation.data.current_count) }}</small>
              </div>
              <div>
                <span>Recorded Activity</span>
                <strong>{{ formatDifference(countExplanation.data.activity_net_change) }}</strong>
                <small>Between count snapshots</small>
              </div>
              <div v-if="Number(countExplanationPendingChange(countExplanation.data) || 0) !== 0" class="count-explanation-summary__pending">
                <span>Pending Adjustments</span>
                <strong>{{ formatDifference(countExplanationPendingChange(countExplanation.data)) }}</strong>
                <small>Sold · waiting for approval</small>
              </div>
              <div :class="{ 'count-explanation-summary__matched': Number(countExplanationUnexplainedChange(countExplanation.data) || 0) === 0 }">
                <span>Unexplained</span>
                <strong>{{ formatDifference(countExplanationUnexplainedChange(countExplanation.data)) }}</strong>
                <small>{{ Number(countExplanationUnexplainedChange(countExplanation.data) || 0) === 0 ? 'Fully matched' : 'Needs review' }}</small>
              </div>
            </div>

            <div class="count-explanation-period">
              Recorded activity from {{ formatDateTime(countExplanation.data.previous_count_date) }} to {{ formatDateTime(countExplanation.data.current_count_date) }}
            </div>

            <div v-if="countExplanation.data.movements?.length" class="count-explanation-movements">
              <div
                v-for="movement in countExplanation.data.movements"
                :key="movement.id"
                class="count-explanation-movement"
                :class="{ 'count-explanation-movement--technical': Number(movement.excluded_from_count_activity) === 1 }"
              >
                <div>
                  <strong>{{ movementTypeLabel(movement.movement_type) }}</strong>
                  <span>{{ movement.buyer_name || movement.reason || 'Inventory activity' }} · {{ formatDateTime(movement.sale_date || movement.created_at) }}</span>
                  <small v-if="movement.notes">{{ movement.notes }}</small>
                  <small v-if="Number(movement.excluded_from_count_activity) === 1">Technical stock repair · excluded from physical activity</small>
                </div>
                <strong :class="Number(movement.quantity_change) >= 0 ? 'movement-positive' : 'movement-negative'">
                  {{ formatDifference(movement.quantity_change) }}
                </strong>
              </div>
            </div>
            <div v-else class="count-explanation-empty">
              No inventory movements were recorded between these counts.
            </div>
            <div v-if="countExplanation.data.pending_adjustments?.length" class="count-explanation-pending">
              <strong>Pending Adjustments</strong>
              <div v-for="pending in countExplanation.data.pending_adjustments" :key="pending.id">
                <span>{{ pending.model_name || 'Model' }} · {{ pending.description }} · {{ formatDate(pending.report_date) }}</span>
                <b>{{ formatDifference(-Number(pending.inventory_quantity || 0)) }}</b>
              </div>
            </div>
            <v-alert
              v-if="canResolveCountFinding(countExplanation.data)"
              class="count-resolution-prompt"
              color="warning"
              icon="mdi-clipboard-search-outline"
              variant="tonal"
            >
              <div class="count-resolution-prompt__content">
                <div>
                  <strong>Found the answer later?</strong>
                  <span v-if="Number(countExplanation.data?.count_correction?.quantity_change || 0) < 0">Assign this missing stock to a model or the studio without deducting inventory again.</span>
                  <span v-else>Explain returned or newly found stock without adding inventory a second time.</span>
                </div>
                <v-btn color="primary" size="small" variant="flat" @click="openResolutionDialog">
                  Resolve Finding
                </v-btn>
              </div>
            </v-alert>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resolutionDialog.open" max-width="560">
      <v-card>
        <v-card-title class="dialog-title">Resolve Inventory Finding</v-card-title>
        <v-card-text>
          <div class="resolution-item-summary">
            <strong>{{ countExplanation.data?.item_name }}</strong>
            <span v-if="resolutionDialog.quantityChange < 0">{{ resolutionDialog.quantity }} missing {{ resolutionDialog.quantity === 1 ? 'unit' : 'units' }} · stock is already corrected</span>
            <span v-else>{{ resolutionDialog.quantity }} extra {{ resolutionDialog.quantity === 1 ? 'unit' : 'units' }} · stock is already corrected</span>
          </div>
          <v-select
            v-model="resolutionDialog.resolutionType"
            :items="resolutionTypesForFinding"
            item-title="label"
            item-value="value"
            label="What happened?"
            variant="outlined"
          ></v-select>
          <template v-if="resolutionDialog.quantityChange < 0 && resolutionDialog.resolutionType === 'model_purchase'">
            <v-select
              v-model="resolutionDialog.modelId"
              :items="resolutionDialog.models"
              :loading="resolutionDialog.loading"
              item-title="title"
              item-value="id"
              label="Model"
              no-data-text="No active models found"
              variant="outlined"
            ></v-select>
            <v-row>
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model.number="resolutionDialog.amount"
                  label="Amount to charge"
                  min="0"
                  prefix="$"
                  suffix="COP"
                  type="number"
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="5">
                <v-select
                  v-model="resolutionDialog.quotas"
                  :items="[1, 2, 3, 4, 5, 6]"
                  label="Quotas"
                  variant="outlined"
                ></v-select>
              </v-col>
            </v-row>
            <v-alert class="mb-4" color="info" variant="tonal">
              This creates a Pending Adjustment. The existing count correction becomes its stock movement, so inventory is not reduced twice.
            </v-alert>
          </template>
          <v-alert v-else-if="resolutionDialog.quantityChange < 0" class="mb-4" color="info" variant="tonal">
            This records the missing item as {{ studioAccountLabel }} use at its internal cost. It does not create sales or income.
          </v-alert>
          <v-alert v-else class="mb-4" color="success" variant="tonal">
            The physical count already added the stock. Saving only explains and classifies that existing +{{ resolutionDialog.quantity }} movement; it will not add the item again.
          </v-alert>
          <v-textarea
            v-model="resolutionDialog.notes"
            label="Observation"
            placeholder="Replacement, gift, forgotten purchase, etc."
            rows="2"
            variant="outlined"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="resolutionDialog.saving" variant="text" @click="resolutionDialog.open = false">Cancel</v-btn>
          <v-btn color="primary" :loading="resolutionDialog.saving" @click="saveFindingResolution">Save Resolution</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2800">{{ snackbar.message }}</v-snackbar>
  </v-card>
</template>

<script>
import secureAxios from '@/utils/secureAxios';

export default {
  name: 'InventoryCounts',
  props: {
    canEdit: { type: Boolean, default: true },
    categories: { type: Array, default: () => [] },
    locations: { type: Array, default: () => [] },
    refreshKey: { type: Number, default: 0 },
    studioId: { type: Number, default: null },
  },
  emits: ['changed'],
  data() {
    return {
      counts: [],
      loading: false,
      saving: false,
      createDialog: false,
      selectedCount: null,
      countView: 'location',
      createForm: { mode: 'full', categoryId: null, locationId: null, notes: '' },
      countModes: [
        { label: 'Full Inventory', value: 'full' },
        { label: 'One Category', value: 'category' },
        { label: 'One Location', value: 'location' },
      ],
      adjustmentReasons: ['Pending adjustment', 'Unrecorded model purchase', 'Temporary loan / returned', 'Returned to stock (charge remains)', 'Restock was not recorded', 'Damage', 'Loss', 'Internal studio use', 'Receiving mistake', 'Counting mistake', 'Needs investigation', 'Other'],
      editingCompletedCount: false,
      originalCompletedQuantities: {},
      editNotes: '',
      countExplanation: { open: false, loading: false, line: null, data: null },
      showCompletedFindings: true,
      missingResolutionTypes: [
        { label: 'Model Purchase', value: 'model_purchase' },
        { label: 'Studio Use', value: 'studio_use' },
      ],
      foundResolutionTypes: [
        { label: 'Returned to stock (charge remains)', value: 'returned_to_stock' },
        { label: 'Restock was not recorded', value: 'unrecorded_restock' },
        { label: 'Purchase already reflected in previous count', value: 'purchase_already_counted' },
        { label: 'Previous count was wrong', value: 'counting_mistake' },
        { label: 'Other found stock', value: 'other_addition' },
      ],
      resolutionDialog: {
        open: false,
        loading: false,
        saving: false,
        resolutionType: 'model_purchase',
        modelId: null,
        amount: 0,
        quotas: 1,
        quantity: 1,
        quantityChange: -1,
        notes: '',
        models: [],
      },
      snackbar: { show: false, message: '', color: 'primary' },
    };
  },
  computed: {
    isMobile() {
      return Boolean(this.$vuetify.display.smAndDown);
    },
    selectedCountIndex() {
      if (!this.selectedCount) return -1;
      return this.counts.findIndex(count => Number(count.id) === Number(this.selectedCount.id));
    },
    canGoOlder() {
      return this.selectedCountIndex >= 0 && this.selectedCountIndex < this.counts.length - 1;
    },
    canGoNewer() {
      return this.selectedCountIndex > 0;
    },
    isCounting() {
      return this.selectedCount?.status === 'counting';
    },
    isPendingReview() {
      return this.selectedCount?.status === 'pending_review';
    },
    isCompleted() {
      return this.selectedCount?.status === 'completed';
    },
    isLatestCountOverall() {
      return Boolean(this.selectedCount && this.counts.length && Number(this.selectedCount.id) === Number(this.counts[0].id));
    },
    previousDateLabel() {
      return this.selectedCount?.previous_count_date ? this.formatDate(this.selectedCount.previous_count_date) : 'No earlier count';
    },
    currentDateLabel() {
      return this.formatDate(this.selectedCount?.started_at || this.selectedCount?.created_at);
    },
    studioAccountLabel() {
      const studios = { 1: 'Studio CC', 2: 'Studio CJ', 3: 'Studio BOG', 4: 'Studio PEI' };
      return studios[Number(this.studioId)] || 'the studio';
    },
    reviewLines() {
      if (!this.isPendingReview) return [];
      return (this.selectedCount?.lines || []).filter(line => this.currentStockDifference(line) !== 0);
    },
    attentionLines() {
      return this.reviewLines
        .filter(line => this.lineReconciliationStatus(line) !== 'matched')
        .sort((left, right) => {
          const rank = { unexplained: 0, pending: 1 };
          return (rank[this.lineReconciliationStatus(left)] ?? 2)
            - (rank[this.lineReconciliationStatus(right)] ?? 2);
        });
    },
    unresolvedCompletedLines() {
      if (!this.isCompleted) return [];
      return (this.selectedCount?.lines || [])
        .filter(line => Number(this.lineUnexplainedChange(line) || 0) !== 0)
        .sort((left, right) => Math.abs(Number(this.lineUnexplainedChange(right) || 0))
          - Math.abs(Number(this.lineUnexplainedChange(left) || 0)));
    },
    completedFindingLines() {
      if (!this.isCompleted) return [];
      return (this.selectedCount?.lines || []).filter(line => (
        Number(line.count_change || 0) !== 0
        || Boolean(line.adjustment_reason)
        || Boolean(line.notes)
      ));
    },
    resolutionTypesForFinding() {
      return Number(this.resolutionDialog.quantityChange || 0) > 0
        ? this.foundResolutionTypes
        : this.missingResolutionTypes;
    },
    groupedCountLines() {
      const lines = Array.isArray(this.selectedCount?.lines) ? [...this.selectedCount.lines] : [];
      const byLocation = this.countView === 'location';
      const labelField = byLocation ? 'location_name' : 'category_name';
      const fallback = byLocation ? 'No location' : 'Uncategorized';
      const sortOrderField = byLocation ? 'location_sort_order' : 'category_sort_order';
      const groups = new Map();

      lines.forEach(line => {
        const label = line[labelField] || fallback;
        if (!groups.has(label)) {
          groups.set(label, {
            key: `${this.countView}-${label}`,
            label,
            sortOrder: Number(line[sortOrderField] || 0),
            lines: [],
          });
        }
        groups.get(label).lines.push(line);
      });

      const sortedGroups = [...groups.values()].sort((left, right) => {
        const orderDifference = left.sortOrder - right.sortOrder;
        if (orderDifference !== 0) return orderDifference;
        return left.label.localeCompare(right.label, undefined, { sensitivity: 'base', numeric: true });
      });

      sortedGroups.forEach(group => {
        group.lines.sort((left, right) => {
          if (byLocation) {
            const leftOrder = Number(left.display_order || 0);
            const rightOrder = Number(right.display_order || 0);
            if ((leftOrder === 0) !== (rightOrder === 0)) return leftOrder === 0 ? 1 : -1;
            if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          } else {
            const locationOrder = Number(left.location_sort_order || 0) - Number(right.location_sort_order || 0);
            if (locationOrder !== 0) return locationOrder;
            const locationName = String(left.location_name || '').localeCompare(String(right.location_name || ''), undefined, { sensitivity: 'base', numeric: true });
            if (locationName !== 0) return locationName;
            const displayOrder = Number(left.display_order || 0) - Number(right.display_order || 0);
            if (displayOrder !== 0) return displayOrder;
          }
          return String(left.item_name || '').localeCompare(String(right.item_name || ''), undefined, { sensitivity: 'base', numeric: true });
        });
      });

      let stripeIndex = 0;
      sortedGroups.forEach(group => {
        group.lines.forEach(line => {
          line.stripeIndex = stripeIndex;
          stripeIndex += 1;
        });
      });

      return sortedGroups;
    },
  },
  watch: {
    refreshKey() { this.loadCounts(true); },
  },
  async mounted() {
    await this.loadCounts(false);
  },
  methods: {
    async loadCounts(preserveSelection = true) {
      this.loading = true;
      try {
        const response = await secureAxios.get('/api/inventory/counts');
        this.counts = response.data || [];
        const preservedId = preserveSelection ? this.selectedCount?.id : null;
        const preserved = this.counts.find(count => Number(count.id) === Number(preservedId));
        const target = preserved || this.counts[0];
        if (target) await this.openCount(target.id);
        else this.selectedCount = null;
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to load count sessions', 'error');
      } finally {
        this.loading = false;
      }
    },
    async openCount(id) {
      try {
        const response = await secureAxios.get(`/api/inventory/counts/${id}`);
        const count = response.data;
        count.lines = count.lines.map(line => ({
          ...line,
          countedQuantity: line.counted_quantity === null ? null : Number(line.counted_quantity),
          resolutionReason: line.adjustment_reason
            || (line.reconciliation_status === 'pending' ? 'Pending adjustment' : ''),
          resolutionNotes: line.notes || '',
        }));
        this.selectedCount = count;
        this.editingCompletedCount = false;
        this.originalCompletedQuantities = {};
        this.editNotes = '';
        this.showCompletedFindings = true;
        this.countView = count.count_mode === 'category' ? 'category' : 'location';
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to open count', 'error');
      }
    },
    async goToOlderCount() {
      if (!this.canGoOlder) return;
      await this.openCount(this.counts[this.selectedCountIndex + 1].id);
    },
    async goToNewerCount() {
      if (!this.canGoNewer) return;
      await this.openCount(this.counts[this.selectedCountIndex - 1].id);
    },
    openCreateDialog() {
      this.createForm = {
        mode: 'full',
        categoryId: null,
        locationId: null,
        notes: '',
      };
      this.createDialog = true;
    },
    async createCount() {
      this.saving = true;
      try {
        const response = await secureAxios.post('/api/inventory/counts', this.createForm);
        this.createDialog = false;
        await this.loadCounts(false);
        await this.openCount(response.data.id);
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to start count', 'error');
      } finally {
        this.saving = false;
      }
    },
    async submitCount() {
      if (this.selectedCount.lines.some(line => line.countedQuantity === null || line.countedQuantity === '' || !Number.isInteger(Number(line.countedQuantity)) || Number(line.countedQuantity) < 0)) {
        return this.notify('Every item needs a valid physical count', 'warning');
      }
      this.saving = true;
      try {
        await secureAxios.post(`/api/inventory/counts/${this.selectedCount.id}/submit`, {
          lines: this.selectedCount.lines.map(line => ({ id: line.id, countedQuantity: Number(line.countedQuantity) })),
        });
        this.notify('Dated count saved', 'success');
        await this.loadCounts(true);
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save count', 'error');
      } finally {
        this.saving = false;
      }
    },
    async approveCount() {
      if (this.selectedCount.lines.some(line => line.countedQuantity === null || line.countedQuantity === '' || !Number.isInteger(Number(line.countedQuantity)) || Number(line.countedQuantity) < 0)) {
        return this.notify('Every item needs a valid physical count', 'warning');
      }
      const missingResolution = this.reviewLines.find(line => !line.resolutionReason);
      if (missingResolution) {
        return this.notify(`Choose what happened with ${missingResolution.item_name}`, 'warning');
      }
      const mismatchedPending = this.reviewLines.find(line => (
        line.resolutionReason === 'Pending adjustment'
        && this.currentStockDifference(line) !== this.linePendingChange(line)
      ));
      if (mismatchedPending) {
        return this.notify(
          `${mismatchedPending.item_name}: count difference ${this.formatDifference(this.currentStockDifference(mismatchedPending))} does not match pending ${this.formatDifference(this.linePendingChange(mismatchedPending))}. Choose another resolution or reload.`,
          'warning'
        );
      }
      this.saving = true;
      try {
        await secureAxios.post(`/api/inventory/counts/${this.selectedCount.id}/approve`, {
          lines: this.selectedCount.lines.map(line => ({
            id: line.id,
            countedQuantity: Number(line.countedQuantity),
            reason: this.currentStockDifference(line) === 0 ? null : line.resolutionReason,
            notes: this.currentStockDifference(line) === 0 ? null : line.resolutionNotes,
          })),
        });
        this.notify('Physical count applied to inventory', 'success');
        await this.loadCounts(true);
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to apply physical count', 'error');
      } finally {
        this.saving = false;
      }
    },
    beginEditCompletedCount() {
      if (!this.isCompleted || !this.isLatestCountOverall) {
        return this.notify('Only the latest completed count can be edited', 'warning');
      }
      this.originalCompletedQuantities = Object.fromEntries(
        this.selectedCount.lines.map(line => [line.id, Number(line.counted_quantity || 0)])
      );
      this.selectedCount.lines.forEach(line => {
        line.countedQuantity = Number(line.counted_quantity || 0);
      });
      this.editNotes = '';
      this.editingCompletedCount = true;
    },
    cancelEditCompletedCount() {
      this.selectedCount.lines.forEach(line => {
        const original = this.originalCompletedQuantities[line.id];
        line.countedQuantity = original;
      });
      this.editingCompletedCount = false;
      this.originalCompletedQuantities = {};
      this.editNotes = '';
    },
    async saveCompletedCountCorrection() {
      if (this.selectedCount.lines.some(line => line.countedQuantity === null || line.countedQuantity === '' || !Number.isInteger(Number(line.countedQuantity)) || Number(line.countedQuantity) < 0)) {
        return this.notify('Every item needs a valid corrected count', 'warning');
      }
      this.saving = true;
      try {
        const response = await secureAxios.put(`/api/inventory/counts/${this.selectedCount.id}/correct`, {
          lines: this.selectedCount.lines.map(line => ({ id: line.id, countedQuantity: Number(line.countedQuantity) })),
          notes: this.editNotes,
        });
        this.notify(response.data?.message || 'Completed count corrected', 'success');
        this.editingCompletedCount = false;
        await this.loadCounts(true);
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to correct completed count', 'error');
      } finally {
        this.saving = false;
      }
    },
    displayedLineChange(line) {
      if (!this.editingCompletedCount && !this.isPendingReview) return line.count_change;
      if (line.previous_counted_quantity === null || line.previous_counted_quantity === undefined) return null;
      return Number(line.countedQuantity || 0) - Number(line.previous_counted_quantity);
    },
    currentStockDifference(line) {
      if (line?.countedQuantity === null || line?.countedQuantity === undefined) return 0;
      return Number(line.countedQuantity) - Number(line.expected_quantity || 0);
    },
    lineUnexplainedChange(line) {
      if (!this.isPendingReview) return Number(line?.unexplained_change || 0);
      return this.currentStockDifference(line) - this.linePendingChange(line);
    },
    linePendingChange(line) {
      return Number(this.isPendingReview
        ? line?.outstanding_pending_net_change
        : line?.pending_net_change) || 0;
    },
    resolutionReasonsForLine(line) {
      const pendingMatches = this.linePendingChange(line) !== 0
        && this.linePendingChange(line) === this.currentStockDifference(line);
      const difference = this.currentStockDifference(line);
      return this.adjustmentReasons.filter(reason => {
        if (reason === 'Pending adjustment') return pendingMatches;
        if (reason === 'Restock was not recorded') return difference > 0;
        return true;
      });
    },
    lineReconciliationStatus(line) {
      const unexplained = this.lineUnexplainedChange(line);
      if (unexplained !== 0) return 'unexplained';
      if (this.linePendingChange(line) !== 0) return 'pending';
      return 'matched';
    },
    lineReconciliationTitle(line) {
      const status = this.lineReconciliationStatus(line);
      if (status === 'matched') return 'Recorded activity matches this change';
      if (status === 'pending') return 'This change is covered by a Pending Adjustment';
      return `${this.formatDifference(this.lineUnexplainedChange(line))} remains unexplained`;
    },
    lineReconciliationColor(line) {
      return { matched: 'success', pending: 'info', unexplained: 'warning' }[this.lineReconciliationStatus(line)];
    },
    lineReconciliationIcon(line) {
      return {
        matched: 'mdi-check-circle-outline',
        pending: 'mdi-clock-outline',
        unexplained: 'mdi-alert-circle-outline',
      }[this.lineReconciliationStatus(line)];
    },
    attentionStatusLabel(line) {
      const status = this.lineReconciliationStatus(line);
      if (status === 'pending') return `Pending ${this.formatDifference(this.linePendingChange(line))}`;
      return `Unexplained ${this.formatDifference(this.lineUnexplainedChange(line))}`;
    },
    completedFindingResolutionLabel(line) {
      if (line?.adjustment_reason) return line.adjustment_reason;
      const status = this.lineReconciliationStatus(line);
      if (status === 'pending') return 'Pending adjustment';
      if (status !== 'matched') return 'Needs review';

      const activityTypes = Array.isArray(line?.activity_types) ? line.activity_types : [];
      const matchedLabels = {
        model_sale: 'Matched: Model purchase',
        direct_sale: 'Matched: Direct sale',
        internal_use: 'Matched: Internal studio use',
        receipt: 'Matched: Restock / receipt',
        return: 'Matched: Return',
      };
      if (activityTypes.length === 1 && matchedLabels[activityTypes[0]]) {
        return matchedLabels[activityTypes[0]];
      }
      return 'Matched recorded activity';
    },
    completedFindingNeedsReview(line) {
      const reason = String(line?.adjustment_reason || '').trim();
      const resolvableReasons = ['', 'Needs investigation', 'Other', 'Unrecorded model purchase', 'Internal studio use'];
      return this.lineReconciliationStatus(line) === 'unexplained'
        && resolvableReasons.includes(reason);
    },
    completedFindingActionLabel(line) {
      return this.completedFindingNeedsReview(line) ? 'Review' : 'Details';
    },
    countExplanationPendingChange(data) {
      return Number(this.isPendingReview
        ? data?.outstanding_pending_net_change
        : data?.pending_net_change) || 0;
    },
    countExplanationUnexplainedChange(data) {
      return Number(this.isPendingReview
        ? data?.stock_unexplained_change
        : data?.unexplained_change) || 0;
    },
    canExplainLine(line) {
      return !this.isCounting
        && !this.editingCompletedCount
        && line.previous_counted_quantity !== null
        && line.previous_counted_quantity !== undefined;
    },
    async openCountExplanation(line) {
      this.countExplanation = { open: true, loading: true, line, data: null };
      try {
        const response = await secureAxios.get(
          `/api/inventory/counts/${this.selectedCount.id}/items/${line.item_id}/activity`
        );
        this.countExplanation.data = response.data;
      } catch (error) {
        this.countExplanation.open = false;
        this.notify(error.response?.data?.message || 'Unable to explain this count change', 'error');
      } finally {
        this.countExplanation.loading = false;
      }
    },
    canResolveCountFinding(data) {
      const reason = String(this.countExplanation.line?.adjustment_reason || '').trim();
      const resolvableReasons = ['', 'Needs investigation', 'Other', 'Unrecorded model purchase', 'Internal studio use'];
      return this.canEdit
        && this.isCompleted
        && Number(this.countExplanationUnexplainedChange(data) || 0) !== 0
        && resolvableReasons.includes(reason)
        && Number(data?.count_correction?.quantity_change || 0) !== 0;
    },
    async openResolutionDialog() {
      const data = this.countExplanation.data;
      if (!this.canResolveCountFinding(data)) return;
      const quantityChange = Number(data.count_correction.quantity_change || 0);
      const quantity = Math.abs(quantityChange);
      const existingReason = String(this.countExplanation.line?.adjustment_reason || '').trim();
      this.resolutionDialog = {
        open: true,
        loading: true,
        saving: false,
        resolutionType: quantityChange > 0
          ? 'returned_to_stock'
          : (existingReason === 'Internal studio use' ? 'studio_use' : 'model_purchase'),
        modelId: null,
        amount: Number(data.default_selling_price || 0) * quantity,
        quotas: 1,
        quantity,
        quantityChange,
        notes: '',
        models: [],
      };
      if (quantityChange > 0) {
        this.resolutionDialog.loading = false;
        return;
      }
      try {
        const response = await secureAxios.get('/api/models', { params: { status: 'Active' } });
        this.resolutionDialog.models = (response.data || [])
          .map(model => ({
            id: Number(model.id),
            title: model.stageName
              ? `${model.fullName} · ${model.stageName}`
              : model.fullName,
          }))
          .sort((left, right) => left.title.localeCompare(right.title, undefined, { sensitivity: 'base' }));
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to load active models', 'error');
      } finally {
        this.resolutionDialog.loading = false;
      }
    },
    async saveFindingResolution() {
      const dialog = this.resolutionDialog;
      if (dialog.quantityChange < 0 && dialog.resolutionType === 'model_purchase' && !dialog.modelId) {
        return this.notify('Choose the model who received the item', 'warning');
      }
      if (dialog.quantityChange < 0 && dialog.resolutionType === 'model_purchase' && Number(dialog.amount || 0) <= 0) {
        return this.notify('Enter the amount to charge', 'warning');
      }
      dialog.saving = true;
      try {
        const response = await secureAxios.post(
          `/api/inventory/counts/${this.selectedCount.id}/lines/${this.countExplanation.line.id}/resolve`,
          {
            resolutionType: dialog.resolutionType,
            modelId: dialog.modelId,
            amount: Number(dialog.amount || 0),
            quotas: Number(dialog.quotas || 1),
            notes: dialog.notes,
          }
        );
        dialog.open = false;
        this.countExplanation.open = false;
        this.notify(response.data?.message || 'Inventory finding resolved', 'success');
        await this.loadCounts(true);
        this.$emit('changed');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to resolve this finding', 'error');
      } finally {
        dialog.saving = false;
      }
    },
    movementTypeLabel(type) {
      return {
        model_sale: 'Model Purchase',
        direct_sale: 'Direct Sale',
        receipt: 'Restock / Receipt',
        return: 'Return',
        damage: 'Damage',
        loss: 'Loss',
        internal_use: 'Internal Studio Use',
        manual_add: 'Manual Addition',
        manual_remove: 'Manual Removal',
        count_correction: 'Count Correction',
        reversal: 'Reversal',
        opening: 'Opening Stock',
      }[type] || String(type || 'Inventory Activity').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    },
    focusNextCountInput(event) {
      const sheet = event?.target?.closest?.('.count-sheet-wrap');
      const inputs = sheet ? [...sheet.querySelectorAll('.count-input input')] : [];
      const currentIndex = inputs.indexOf(event?.target);
      const nextInput = currentIndex >= 0 ? inputs[currentIndex + 1] : null;
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      } else if (typeof event?.target?.blur === 'function') {
        event.target.blur();
      }
    },
    disableCountWheel(event) {
      if (typeof event?.target?.blur === 'function') event.target.blur();
    },
    printCountSheet() {
      const popup = window.open('', '_blank', 'width=1000,height=760');
      if (!popup) return this.notify('Allow pop-ups to print the count sheet.', 'warning');
      const blind = this.isCounting;
      const rows = this.groupedCountLines.flatMap(group => [
        `<tr class="group"><td colspan="4">${this.escapePrintText(group.label)}</td></tr>`,
        ...group.lines.map(line => `
          <tr>
            <td>${this.escapePrintText(line.item_name)}</td>
            <td>${blind ? '' : this.escapePrintText(this.displayCount(line.previous_counted_quantity))}</td>
            <td>${blind ? '' : this.escapePrintText(this.displayCount(line.counted_quantity))}</td>
            <td>${blind ? '' : this.escapePrintText(this.formatDifference(line.count_change))}</td>
          </tr>`),
      ]).join('');
      popup.document.write(`<!doctype html><html><head><title>Inventory Count ${this.currentDateLabel}</title><style>
        body{font-family:Arial,sans-serif;color:#173553;margin:26px}h1{margin:0 0 5px;font-size:22px}.meta{margin-bottom:18px;color:#607589}
        table{width:100%;border-collapse:collapse}th,td{padding:9px 10px;border-bottom:1px solid #dce6ef;text-align:left}th:not(:first-child),td:not(:first-child){text-align:center}
        th{background:#145a92;color:white}.group td{background:#eaf3fa;font-weight:700;text-align:left!important;border-top:2px solid #9fb8cd}
        @media print{body{margin:12mm}}
      </style></head><body><h1>Inventory Count</h1><div class="meta">${this.escapePrintText(this.currentDateLabel)} · ${this.escapePrintText(this.scopeLabel(this.selectedCount))}</div>
      <table><thead><tr><th>Item</th><th>Previous</th><th>Current</th><th>Change</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
      popup.document.close();
      popup.focus();
      popup.print();
    },
    escapePrintText(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    },
    displayCount(value) {
      return value === null || value === undefined || value === '' ? '—' : Number(value);
    },
    changeClass(value) {
      if (value === null || value === undefined) return '';
      if (Number(value) < 0) return 'change-negative';
      if (Number(value) > 0) return 'change-positive';
      return 'change-zero';
    },
    formatDifference(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
      const number = Number(value);
      return number > 0 ? `+${number}` : String(number);
    },
    scopeLabel(count) {
      if (count.count_mode === 'category') return count.category_name || 'Category';
      if (count.count_mode === 'location') return count.location_name || 'Location';
      return 'Full Inventory';
    },
    statusLabel(status) {
      return { draft: 'Draft', counting: 'Counting', pending_review: 'Saved · Review', completed: 'Completed', cancelled: 'Cancelled' }[status] || status;
    },
    statusColor(status) {
      return { counting: 'primary', pending_review: 'warning', completed: 'success', cancelled: 'grey' }[status] || 'grey';
    },
    formatDate(value) {
      if (!value) return '—';
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
    },
    formatDateTime(value) {
      if (!value) return 'Unknown date';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }).format(date);
    },
    notify(message, color = 'primary') {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>

<style scoped>
.counts-panel { border: 1px solid #dfe7f0; border-radius: 10px; overflow: hidden; }
.counts-panel__heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px 22px; border-bottom: 1px solid #edf1f5; }
.counts-panel h2 { margin: 0; color: #173553; font-size: 17px; }
.counts-panel p { margin: 4px 0 0; color: #6b7f93; font-size: 13px; }
.counts-panel__controls, .count-toolbar, .count-history-nav, .count-toolbar__actions { display: flex; align-items: center; gap: 10px; }
.recent-counts { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 12px 18px; border-bottom: 1px solid #e4ebf2; }
.recent-counts > span { color: #6b7f93; font-size: 12px; font-weight: 700; }
.counts-panel__empty { display: flex; min-height: 360px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; }
.count-toolbar { justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid #e4ebf2; background: #f8fafc; }
.count-history-nav__label { display: flex; min-width: 150px; flex-direction: column; text-align: center; }
.count-history-nav__label span { color: #173553; font-size: 14px; font-weight: 800; }
.count-history-nav__label small { color: #718397; font-size: 10px; }
.latest-label { color: #27845b; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.count-alert { margin: 16px 18px 0; }
.count-attention-card { margin: 16px 18px 0; overflow: hidden; border: 1px solid #ead8a8; border-radius: 9px; background: #fff; }
.count-attention-card__heading { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 14px; background: #fff9ea; }
.count-attention-card__heading > div { display: flex; align-items: center; gap: 9px; }
.count-attention-card__heading > div > div { display: flex; flex-direction: column; }
.count-attention-card__heading strong { color: #594615; font-size: 13px; }
.count-attention-card__heading span { color: #806d3d; font-size: 11px; }
.count-attention-card__items { display: grid; grid-template-columns: repeat(auto-fit, minmax(235px, 1fr)); }
.count-attention-item { display: flex; min-width: 0; align-items: center; justify-content: space-between; gap: 10px; padding: 11px 14px; border: 0; border-top: 1px solid #eee5cf; border-right: 1px solid #eee5cf; background: #fff; text-align: left; cursor: pointer; }
.count-attention-item:hover { background: #fffcf4; }
.count-attention-item > div { display: flex; min-width: 0; flex-direction: column; }
.count-attention-item strong { overflow: hidden; color: #2b4258; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.count-attention-item span { color: #7b6c48; font-size: 10px; }
.count-sheet-wrap { margin: 16px 18px 20px; overflow-x: auto; border: 1px solid #cddbe7; border-radius: 9px; }
.count-sheet { width: 100%; min-width: 760px; border-collapse: collapse; table-layout: fixed; }
.count-sheet th { padding: 11px 14px; border-right: 1px solid rgba(255,255,255,.18); background: #514a43; color: #fff; font-size: 11px; letter-spacing: .04em; text-align: left; text-transform: uppercase; }
.count-sheet th:last-child { border-right: 0; }
.count-sheet th span, .count-sheet th small { display: block; }
.count-sheet th small { margin-top: 3px; color: rgba(255,255,255,.72); font-size: 9px; font-weight: 500; letter-spacing: 0; text-transform: none; }
.item-column { width: 52%; }
.number-column { width: 16%; text-align: center !important; }
.count-sheet th.current-column { background: #625a52; }
.count-sheet th.change-column { background: #3f3b37; }
.count-group-row td { padding: 0; border-top: 2px solid #b7afa6; border-bottom: 1px solid #d2ccc5; background: #eeeae4; }
.count-group-row:first-child td { border-top: 0; }
.count-group-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 14px; color: #173553; }
.count-group-heading > div { display: flex; align-items: center; gap: 8px; }
.count-group-heading > span { color: #60778e; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.count-item-row td { height: 48px; padding: 7px 14px; border-bottom: 1px solid #e1e7ec; background: #ffffff; color: #334b61; font-size: 13px; }
.count-item-row--alternate td { background: #f0f4f8; }
.count-item-row:last-child td { border-bottom: 0; }
.count-item-row .item-column strong, .count-item-row .item-column span { display: block; }
.count-item-row .item-column span { margin-top: 2px; color: #8493a2; font-size: 10px; }
.count-input { width: 105px; margin: 0 auto; }
.count-input :deep(input) { text-align: center; font-weight: 800; }
.hidden-count { color: #9aa8b5; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.change-negative strong { color: #c23d4b; }
.change-positive strong { color: #27845b; }
.change-zero strong { color: #74869a; }
.count-change-button { display: inline-flex; align-items: center; justify-content: center; gap: 5px; min-width: 44px; padding: 4px 7px; border: 0; border-radius: 6px; background: transparent; cursor: pointer; }
.count-change-button:hover { background: rgba(44, 116, 179, .09); }
.count-change-button strong { font: inherit; font-weight: 800; }
.completed-findings { margin: 0 18px 18px; overflow: hidden; border: 1px solid #cddbe7; border-radius: 9px; background: #fff; }
.completed-findings__heading { display: flex; width: 100%; align-items: center; justify-content: space-between; gap: 14px; padding: 11px 14px; border: 0; background: #f4f8fb; text-align: left; cursor: pointer; }
.completed-findings__heading > div { display: flex; align-items: center; gap: 9px; }
.completed-findings__heading > div > div { display: flex; flex-direction: column; }
.completed-findings__heading strong { color: #173553; font-size: 13px; }
.completed-findings__heading span { color: #718397; font-size: 11px; }
.completed-findings__table-wrap { overflow-x: auto; border-top: 1px solid #dce6ef; }
.completed-findings__table { width: 100%; min-width: 720px; border-collapse: collapse; }
.completed-findings__table th { padding: 8px 10px; background: #fff; color: #718397; font-size: 9px; letter-spacing: .04em; text-align: left; text-transform: uppercase; }
.completed-findings__table td { padding: 9px 10px; border-top: 1px solid #e7edf2; color: #536b80; font-size: 11px; }
.completed-findings__table td:first-child { color: #2b4258; font-size: 12px; }
.completed-findings__table th:nth-child(2), .completed-findings__table td:nth-child(2) { width: 92px; text-align: center; }
.completed-findings__table th:last-child, .completed-findings__table td:last-child { width: 82px; text-align: right; }
.difference-review { margin: 0 18px 18px; overflow: hidden; border: 1px solid #ead8a8; border-radius: 9px; }
.difference-review__heading { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 14px; background: #fff9ea; }
.difference-review__heading > div { display: flex; flex-direction: column; }
.difference-review__heading strong { color: #594615; font-size: 13px; }
.difference-review__heading span { color: #806d3d; font-size: 11px; }
.difference-review__row { display: grid; grid-template-columns: minmax(210px, 1fr) minmax(210px, .8fr) minmax(240px, 1.2fr); align-items: center; gap: 10px; padding: 10px 14px; border-top: 1px solid #eee5cf; background: #fff; }
.difference-review__identity { display: flex; min-width: 0; flex-direction: column; }
.difference-review__identity strong { color: #2b4258; font-size: 12px; }
.difference-review__identity span { color: #7b6c48; font-size: 10px; }
.count-edit-note { margin: 0 18px 18px; }
.count-save-bar { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 15px 18px; border-top: 1px solid #dce6ef; background: #f8fafc; }
.count-save-bar > div { display: flex; flex-direction: column; }
.count-save-bar strong { color: #173553; font-size: 13px; }
.count-save-bar span { color: #718397; font-size: 11px; }
.count-edit-actions { display: flex; align-items: center; gap: 8px; }
.mobile-edit-count { display: none; }
.dialog-title { color: #0a2647; font-size: 18px; font-weight: 700; }
.count-explanation-heading { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e4ebf2; }
.count-explanation-heading > div { display: flex; flex-direction: column; }
.count-explanation-heading span { color: #173553; font-size: 18px; font-weight: 800; }
.count-explanation-heading small { margin-top: 2px; color: #718397; font-size: 12px; font-weight: 500; }
.count-explanation-loading { display: flex; min-height: 260px; align-items: center; justify-content: center; }
.count-explanation-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; margin-bottom: 14px; }
.count-explanation-summary > div { display: flex; min-width: 0; flex-direction: column; padding: 13px 14px; border: 1px solid #e0e7ed; border-radius: 8px; background: #f8fafc; }
.count-explanation-summary span { color: #718397; font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.count-explanation-summary strong { margin-top: 3px; color: #173553; font-size: 21px; }
.count-explanation-summary small { color: #8493a2; font-size: 10px; }
.count-explanation-summary__matched { border-color: #cce8da !important; background: #f3fbf7 !important; }
.count-explanation-summary__matched strong { color: #27845b; }
.count-explanation-summary__pending { border-color: #bfd9ee !important; background: #f1f7fc !important; }
.count-explanation-summary__pending strong { color: #2c74b3 !important; }
.count-explanation-period { margin-bottom: 8px; color: #718397; font-size: 11px; }
.count-explanation-movements { border: 1px solid #e0e7ed; border-radius: 8px; overflow: hidden; }
.count-explanation-movement { display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 58px; padding: 9px 13px; border-bottom: 1px solid #e7edf2; }
.count-explanation-movement--technical { background: #f6f8fa; opacity: 0.78; }
.count-explanation-movement:last-child { border-bottom: 0; }
.count-explanation-movement > div { display: flex; min-width: 0; flex-direction: column; }
.count-explanation-movement > div strong { color: #2b4258; font-size: 12px; }
.count-explanation-movement span, .count-explanation-movement small { color: #7b8b9b; font-size: 10px; }
.movement-positive { color: #27845b; }
.movement-negative { color: #c23d4b; }
.count-explanation-empty { padding: 22px; border: 1px dashed #cfdbe5; border-radius: 8px; color: #718397; font-size: 12px; text-align: center; }
.count-explanation-pending { margin-top: 12px; overflow: hidden; border: 1px solid #bfd9ee; border-radius: 8px; background: #f7fbff; }
.count-explanation-pending > strong { display: block; padding: 9px 12px; color: #2c74b3; font-size: 11px; text-transform: uppercase; }
.count-explanation-pending > div { display: flex; justify-content: space-between; gap: 14px; padding: 9px 12px; border-top: 1px solid #dceaf5; color: #536b80; font-size: 11px; }
.count-explanation-pending b { color: #2c74b3; }
.count-resolution-prompt { margin-top: 12px; }
.count-resolution-prompt__content { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.count-resolution-prompt__content > div { display: flex; flex-direction: column; }
.count-resolution-prompt__content strong { color: #5a4616; font-size: 12px; }
.count-resolution-prompt__content span { color: #7c6a3e; font-size: 10px; }
.resolution-item-summary { display: flex; flex-direction: column; margin-bottom: 16px; padding: 12px 14px; border: 1px solid #dce6ef; border-radius: 8px; background: #f8fafc; }
.resolution-item-summary strong { color: #173553; font-size: 14px; }
.resolution-item-summary span { margin-top: 2px; color: #718397; font-size: 11px; }

@media (max-width: 900px) {
  .counts-panel__heading, .count-toolbar { align-items: stretch; flex-direction: column; }
  .counts-panel__controls, .count-toolbar__actions { flex-wrap: wrap; }
  .count-history-nav { justify-content: center; }
}

@media (max-width: 700px) {
  .counts-panel { border-right: 0; border-left: 0; border-radius: 0; }
  .counts-panel__heading { gap: 12px; padding: 14px 10px; }
  .counts-panel__heading > div:first-child p { display: none; }
  .counts-panel__controls { align-items: stretch; flex-direction: column; }
  .count-toolbar { padding: 10px 6px; }
  .count-toolbar__actions { display: none; }
  .mobile-edit-count { display: block; padding: 8px 10px 0; }
  .count-history-nav { gap: 2px; }
  .count-history-nav__label { min-width: 130px; }
  .count-alert { margin: 8px 6px 0; font-size: 12px; }
  .count-attention-card { margin: 8px 6px 0; }
  .count-attention-card__items { grid-template-columns: 1fr; }
  .count-sheet-wrap { margin: 8px 0 12px; overflow-x: visible; border-right: 0; border-left: 0; border-radius: 0; }
  .count-sheet { min-width: 0; table-layout: fixed; }
  .count-sheet .previous-column,
  .count-sheet .previous-value,
  .count-sheet .change-column { display: none; }
  .count-sheet .item-column { width: 75%; }
  .count-sheet .current-column { width: 25%; }
  .count-sheet th { padding: 9px 10px; }
  .count-group-heading { padding: 9px 10px; }
  .count-item-row td { height: 52px; padding: 6px 10px; }
  .count-item-row .item-column strong { white-space: normal; line-height: 1.25; }
  .count-item-row .item-column span { display: none; }
  .count-input { width: 76px; }
  .count-input :deep(.v-field__input) { min-height: 38px; padding-inline: 4px; font-size: 18px; }
  .difference-review { margin-inline: 10px; }
  .completed-findings { margin-inline: 10px; }
  .completed-findings__heading { align-items: flex-start; }
  .completed-findings__heading span { display: none; }
  .difference-review__heading { align-items: flex-start; }
  .difference-review__row { grid-template-columns: 1fr; }
  .count-save-bar { position: sticky; z-index: 5; bottom: 0; align-items: stretch; flex-direction: column; gap: 8px; padding: 10px; box-shadow: 0 -4px 12px rgba(31, 54, 76, .12); }
  .count-save-bar span { display: none; }
  .count-save-bar .v-btn { width: 100%; }
  .count-edit-actions { width: 100%; flex-direction: column; }
  .count-explanation-summary { grid-template-columns: 1fr; }
  .count-resolution-prompt__content { align-items: stretch; flex-direction: column; }
}
</style>
